import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { StockRegion, StockRegionRow } from "../types";
import { stockRegionRowToRegion } from "../types";

/**
 * Charge les régions de stock du tenant du partenaire/poseur connecté.
 * Avec session (connexion par code → magic link) : RPC get_tenant_stock_regions.
 * Sans session (ancien partenaire) : Edge Function get-partner-stock-regions avec code + role.
 */
export function usePartnerStockRegions(
  enabled: boolean,
  code?: string,
  role?: "partner" | "installer"
) {
  const [regions, setRegions] = useState<StockRegion[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRegions = useCallback(async () => {
    if (!enabled) {
      setRegions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_tenant_stock_regions");
      const rows = (!error && data != null ? (data as StockRegionRow[]) : []) ?? [];
      if (!error && rows.length > 0) {
        setRegions(rows.map(stockRegionRowToRegion));
        setLoading(false);
        return;
      }
      if (code?.trim() && role) {
        const { data: fnData, error: fnErr } = await supabase.functions.invoke("get-partner-stock-regions", {
          body: { code: code.trim().toUpperCase(), role },
        });
        if (!fnErr) {
          const list = (fnData as { regions?: StockRegionRow[] })?.regions ?? [];
          setRegions(list.map(stockRegionRowToRegion));
        }
      } else if (rows.length >= 0) {
        setRegions(rows.map(stockRegionRowToRegion));
      }
    } catch {
      setRegions([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, code, role]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  return { regions, loading, refetch: fetchRegions };
}
